import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CommonModal from '../CommonModal';
import Table from 'apollo-react/components/Table';
import Typography from '../StyledTypography';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Box from 'apollo-react/components/Box';
import Grid from 'apollo-react/components/Grid';
import useFieldWavesApi from '../../../hooks/api/fieldWaves/useFieldWavesApi';
import FileLink from '../FileLink';
import { convertUTCToLocal } from '../../utils/dateUtils';
import { renderDisplayValue } from '../../utils/stringUtils';
import {
  formatDateValue,
  formatSwitchValue,
  SWITCH_VALUES,
  isBase64Signature,
  getBase64ImageSrc,
} from '../../../utils/formCrafter/listViewUtils';
import { formatSelectValue } from '@iqvia-technology-solutions/formcrafter';
import styles from './FieldWavesModal.module.css';

const FILE_TYPES = new Set(['file', 'attachment', 'image']);
const DATE_TYPES = new Set([
  'date',
  'datetime-local',
  'datetime',
  'time',
  'month',
  'week',
]);
const OPTION_TYPES = new Set(['select', 'radio', 'checkbox']);

const FilesCell = ({ files }) => {
  if (!files || files.length === 0) {
    return <span>-</span>;
  }
  return (
    <Box>
      {files.map((file) => (
        <FileLink key={file.id} file={file} includeDeleted />
      ))}
    </Box>
  );
};

const parseJsonIfString = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const isRepeaterValue = (value) => {
  const normalized = parseJsonIfString(value);
  if (!Array.isArray(normalized)) return false;
  if (normalized.length === 0) return false;
  return normalized.every((item) => item && typeof item === 'object');
};

const isFileMetadata = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return (
    'isStagedFile' in value ||
    'stagedId' in value ||
    (typeof value.name === 'string' &&
      value.size !== undefined &&
      typeof value.type === 'string')
  );
};

const isFileMetadataArray = (value) => {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(isFileMetadata);
};

const getRepeaterItemKeys = (obj, fieldSchemaMap) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  const keys = Object.keys(obj).filter((key) => key !== 'id');

  if (fieldSchemaMap && Object.keys(fieldSchemaMap).length > 0) {
    const schemaKeys = keys.filter((k) => k in fieldSchemaMap);
    const extraKeys = keys.filter((k) => !(k in fieldSchemaMap));
    return [...schemaKeys, ...extraKeys];
  }

  return keys;
};

const toFileLinkObj = (file) => {
  const mimeType = file.type || file.mime_type;
  return {
    id: file.id || file.stagedId,
    name: file.name || file.original_name || 'Unknown file',
    mime_type: mimeType, // eslint-disable-line camelcase
  };
};

const FileNameDisplay = ({ value }) => {
  const normalized = parseJsonIfString(value);

  if (isFileMetadata(normalized)) {
    const fileObj = toFileLinkObj(normalized);
    if (fileObj.id) {
      return (
        <div className={styles.fileNameList}>
          <FileLink file={fileObj} />
        </div>
      );
    }
    return <span className={styles.fileName}>{fileObj.name}</span>;
  }

  if (isFileMetadataArray(normalized)) {
    return (
      <div className={styles.fileNameList}>
        {normalized.map((file, i) => {
          const fileObj = toFileLinkObj(file);
          if (fileObj.id) {
            return <FileLink key={fileObj.id} file={fileObj} />;
          }
          return (
            <span key={i} className={styles.fileName}>
              {fileObj.name}
            </span>
          );
        })}
      </div>
    );
  }

  if (Array.isArray(normalized) && normalized.length > 0) {
    const hasNumericIds = normalized.every(
      (item) => typeof item === 'number' || typeof item === 'string'
    );
    if (hasNumericIds) {
      return (
        <div className={styles.fileNameList}>
          {normalized.map((id, i) => (
            <FileLink key={id || i} file={{ id, name: `File ${id}` }} />
          ))}
        </div>
      );
    }
  }

  if (typeof normalized === 'number') {
    return (
      <div className={styles.fileNameList}>
        <FileLink file={{ id: normalized, name: `File ${normalized}` }} />
      </div>
    );
  }

  if (typeof normalized === 'string' && normalized) {
    return <span className={styles.fileName}>{normalized}</span>;
  }

  return <span className={styles.emptyValue}>-</span>;
};

/**
 * Type-aware value renderer for a single field inside a repeater item.
 * Uses the field schema to apply proper formatting.
 */
const RepeaterFieldValue = ({ value, fieldDef, t }) => {
  const normalized = parseJsonIfString(value);
  if (normalized === null || normalized === undefined) {
    return <span className={styles.emptyValue}>-</span>;
  }

  if (typeof normalized === 'string' && normalized === '') {
    return <span className={styles.emptyValue}>-</span>;
  }

  if (isFileMetadata(normalized) || isFileMetadataArray(normalized)) {
    return <FileNameDisplay value={normalized} />;
  }

  const fieldType = fieldDef?.type;

  if (fieldType && FILE_TYPES.has(fieldType)) {
    return <FileNameDisplay value={normalized} />;
  }

  if (fieldType === 'signature') {
    if (typeof normalized === 'string' && isBase64Signature(normalized)) {
      return (
        <img
          src={getBase64ImageSrc(normalized)}
          alt={t('fieldWaves.signature')}
          className={styles.signatureThumbnail}
        />
      );
    }
    return <span className={styles.emptyValue}>-</span>;
  }

  if (fieldType === 'switch') {
    const switchVal = formatSwitchValue(normalized);
    if (switchVal === SWITCH_VALUES.YES)
      return <span>{t('fieldValueRenderer.switch.yes')}</span>;
    if (switchVal === SWITCH_VALUES.NO)
      return <span>{t('fieldValueRenderer.switch.no')}</span>;
    return <span className={styles.emptyValue}>-</span>;
  }

  if (fieldType && DATE_TYPES.has(fieldType)) {
    return <span>{formatDateValue(normalized, fieldType)}</span>;
  }

  if (fieldType && OPTION_TYPES.has(fieldType) && fieldDef) {
    return <span>{formatSelectValue(normalized, fieldDef)}</span>;
  }

  if (fieldType === 'textarea' && typeof normalized === 'string') {
    return <span className={styles.preWrap}>{normalized}</span>;
  }

  if (Array.isArray(normalized)) {
    if (normalized.length === 0)
      return <span className={styles.emptyValue}>-</span>;
    if (isFileMetadataArray(normalized)) {
      return <FileNameDisplay value={normalized} />;
    }
    return <span>{renderDisplayValue(normalized)}</span>;
  }

  if (typeof normalized === 'boolean') {
    const switchVal = formatSwitchValue(normalized);
    if (switchVal === SWITCH_VALUES.YES)
      return <span>{t('fieldValueRenderer.switch.yes')}</span>;
    if (switchVal === SWITCH_VALUES.NO)
      return <span>{t('fieldValueRenderer.switch.no')}</span>;
    return <span className={styles.emptyValue}>-</span>;
  }

  if (typeof normalized === 'object') {
    if (isFileMetadata(normalized)) {
      return <FileNameDisplay value={normalized} />;
    }
    return <span>{renderDisplayValue(normalized)}</span>;
  }

  return <span>{renderDisplayValue(normalized)}</span>;
};

/**
 * Renders a full repeater value (array of items) with schema awareness.
 * Uses fieldLabelMap for labels and fieldSchemaMap for type-specific rendering.
 */
const RepeaterValueDisplay = ({ value, fieldLabelMap, fieldSchemaMap, t }) => {
  const normalized = parseJsonIfString(value);

  if (normalized === null || normalized === undefined) {
    return <span className={styles.emptyValue}>-</span>;
  }

  if (!Array.isArray(normalized) || normalized.length === 0) {
    return <span className={styles.emptyValue}>-</span>;
  }

  const isObjectArray = normalized.every(
    (item) => item && typeof item === 'object'
  );
  if (!isObjectArray) {
    return <span>{renderDisplayValue(normalized)}</span>;
  }

  return (
    <div className={styles.listStack}>
      {normalized.map((item, index) => {
        const keys = getRepeaterItemKeys(item, fieldSchemaMap);
        return (
          <div key={item?.id || index} className={styles.listItem}>
            <div className={styles.listItemTitle}>
              {t('fieldWaves.repeaterItem', { index: index + 1 })}
            </div>
            <div className={styles.kvList}>
              {keys.map((key) => (
                <div key={key} className={styles.kvRow}>
                  <span className={styles.kvKey}>
                    {fieldLabelMap[key] || key}
                  </span>
                  <span className={styles.kvValue}>
                    <RepeaterFieldValue
                      value={item[key]}
                      fieldDef={fieldSchemaMap[key]}
                      t={t}
                    />
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Generic StructuredValue for non-repeater waves (unchanged behavior) ---

const getObjectKeys = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  const keys = Object.keys(obj);
  if (keys.length === 0) return [];
  const nonIdKeys = keys.filter((key) => key !== 'id');
  return nonIdKeys.length > 0 ? nonIdKeys : keys;
};

const StructuredValue = ({ value }) => {
  const normalized = parseJsonIfString(value);

  if (normalized === null || normalized === undefined) {
    return <span>-</span>;
  }

  if (Array.isArray(normalized)) {
    if (normalized.length === 0) return <span>-</span>;
    const isObjectArray = normalized.every(
      (item) => item && typeof item === 'object'
    );
    if (!isObjectArray) {
      return <span>{renderDisplayValue(normalized)}</span>;
    }

    return (
      <div className={styles.listStack}>
        {normalized.map((item, index) => {
          const itemLabel =
            item?.item_field ||
            item?.label ||
            item?.name ||
            `Item ${index + 1}`;
          const keys = getObjectKeys(item);
          return (
            <div key={item?.id || index} className={styles.listItem}>
              <div className={styles.listItemTitle}>{itemLabel}</div>
              <div className={styles.kvList}>
                {keys.map((key) => (
                  <div key={key} className={styles.kvRow}>
                    <span className={styles.kvKey}>{key}</span>
                    <span className={styles.kvValue}>
                      <StructuredValue value={item[key]} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (typeof normalized === 'object') {
    const keys = getObjectKeys(normalized);
    return (
      <div className={styles.kvList}>
        {keys.map((key) => (
          <div key={key} className={styles.kvRow}>
            <span className={styles.kvKey}>{key}</span>
            <span className={styles.kvValue}>
              <StructuredValue value={normalized[key]} />
            </span>
          </div>
        ))}
      </div>
    );
  }

  return <span>{renderDisplayValue(normalized)}</span>;
};

const ValueCell = ({ value, files }) => {
  if (files && files.length > 0) {
    return <FilesCell files={files} />;
  }
  return <StructuredValue value={value} />;
};

/**
 * FieldWavesModal - Modal for viewing field change history (waves)
 *
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {number|string} requestId - The request ID
 * @param {string} fieldKey - The field key (formId_subformId_fieldId)
 * @param {string} [fieldLabel] - Optional field label for the title
 * @param {Array} [repeaterFields] - Optional nested field definitions for type-aware rendering
 */
const FieldWavesModal = ({
  open,
  onClose,
  requestId,
  fieldKey,
  fieldLabel,
  repeaterFields,
}) => {
  const { t } = useTranslation();
  const { getFieldWaves } = useFieldWavesApi();
  const [waves, setWaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { fieldLabelMap, fieldSchemaMap } = useMemo(() => {
    const labelMap = {};
    const schemaMap = {};
    if (Array.isArray(repeaterFields)) {
      repeaterFields.forEach((f) => {
        if (f?.id) {
          if (f.label) labelMap[f.id] = f.label;
          schemaMap[f.id] = f;
        }
      });
    }
    return { fieldLabelMap: labelMap, fieldSchemaMap: schemaMap };
  }, [repeaterFields]);

  const fetchWaves = useCallback(async () => {
    if (!requestId || !fieldKey) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getFieldWaves(requestId, fieldKey);
      setWaves(response.data || []);
    } catch (err) {
      setError(err.message || t('fieldWaves.error'));
    } finally {
      setLoading(false);
    }
  }, [requestId, fieldKey, getFieldWaves, t]);

  useEffect(() => {
    if (open) {
      fetchWaves();
    }
  }, [open, fetchWaves]);

  const renderWaveMeta = (label, value) => (
    <div className={styles.metaRow}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{value || '-'}</span>
    </div>
  );

  const isRepeaterWave = waves.some(
    (wave) => isRepeaterValue(wave.old_value) || isRepeaterValue(wave.new_value)
  );

  const hasSchema = Object.keys(fieldSchemaMap).length > 0;

  const RepeaterWaveValueCell = useCallback(
    ({ value, files }) => {
      if (files && files.length > 0) {
        return <FilesCell files={files} />;
      }
      if (isRepeaterWave && hasSchema) {
        return (
          <RepeaterValueDisplay
            value={value}
            fieldLabelMap={fieldLabelMap}
            fieldSchemaMap={fieldSchemaMap}
            t={t}
          />
        );
      }
      return <StructuredValue value={value} />;
    },
    [isRepeaterWave, hasSchema, fieldLabelMap, fieldSchemaMap, t]
  );

  const columns = [
    {
      header: t('fieldWaves.columns.wave'),
      accessor: 'wave_number',
      width: 80,
    },
    {
      header: t('fieldWaves.columns.old'),
      accessor: 'old_value',
      customCell: ({ row }) => (
        <ValueCell value={row.old_value} files={row.old_files} />
      ),
    },
    {
      header: t('fieldWaves.columns.new'),
      accessor: 'new_value',
      customCell: ({ row }) => (
        <ValueCell value={row.new_value} files={row.new_files} />
      ),
    },
    {
      header: t('fieldWaves.columns.changedBy'),
      accessor: 'changed_by',
      customCell: ({ row }) => <span>{row.changed_by?.email || '-'}</span>,
    },
    {
      header: t('fieldWaves.columns.sentBackBy'),
      accessor: 'sent_back_by',
      customCell: ({ row }) => <span>{row.sent_back_by?.email || '-'}</span>,
    },
    {
      header: t('fieldWaves.columns.date'),
      accessor: 'created_at',
      customCell: ({ row }) => <span>{convertUTCToLocal(row.created_at)}</span>,
    },
  ];

  const modalTitle = fieldLabel
    ? `${t('fieldWaves.title')} - ${fieldLabel}`
    : t('fieldWaves.title');

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      hideButtons
      className={styles.modal}
    >
      <Box className={styles.content}>
        {loading && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            className={styles.loadingContainer}
          >
            <CircularProgress />
          </Grid>
        )}

        {error && !loading && (
          <Typography color="error" className={styles.error}>
            {error}
          </Typography>
        )}

        {!loading && !error && waves.length === 0 && (
          <Typography className={styles.emptyState}>
            {t('fieldWaves.empty')}
          </Typography>
        )}

        {!loading && !error && waves.length > 0 && (
          <>
            {isRepeaterWave ? (
              <div className={styles.wavesList}>
                {waves.map((wave) => (
                  <div key={wave.id} className={styles.waveCard}>
                    <div className={styles.waveHeader}>
                      <span className={styles.waveTitle}>
                        {t('fieldWaves.columns.wave')} {wave.wave_number || '-'}
                      </span>
                      <span className={styles.waveDate}>
                        {convertUTCToLocal(wave.created_at)}
                      </span>
                    </div>
                    <div className={styles.waveBody}>
                      <div className={styles.waveSection}>
                        <span className={styles.sectionLabel}>
                          {t('fieldWaves.columns.old')}
                        </span>
                        <RepeaterWaveValueCell
                          value={wave.old_value}
                          files={wave.old_files}
                        />
                      </div>
                      <div className={styles.waveSection}>
                        <span className={styles.sectionLabel}>
                          {t('fieldWaves.columns.new')}
                        </span>
                        <RepeaterWaveValueCell
                          value={wave.new_value}
                          files={wave.new_files}
                        />
                      </div>
                    </div>
                    <div className={styles.waveMeta}>
                      {renderWaveMeta(
                        t('fieldWaves.columns.changedBy'),
                        wave.changed_by?.email
                      )}
                      {renderWaveMeta(
                        t('fieldWaves.columns.sentBackBy'),
                        wave.sent_back_by?.email
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Table
                columns={columns}
                rows={waves}
                rowId="id"
                hidePagination
                rowProps={{ hover: false }}
                className={styles.table}
              />
            )}
          </>
        )}
      </Box>
    </CommonModal>
  );
};

export default FieldWavesModal;
